import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { Check, ChevronDown, Search, X } from 'lucide-react'

import { useDebounce } from '@/hooks/useDebounce'
import { cn } from '@/utils/common'

import { SEARCH_DEBOUNCE_DELAY_MS } from '../../utils/constants'

import { Input } from './input/input'
import { Button } from './button'
import { Checkbox } from './checkbox'
import type { Option } from './select'

const DEFAULT_PLACEHOLDER = 'Select options'
const DEFAULT_SEARCH_PLACEHOLDER = 'Search...'
const DEFAULT_MAX_HEIGHT = 300
const FOCUS_DELAY_MS = 0
const SELECT_ALL_TEXT = 'Select All'
const CLEAR_ALL_TEXT = 'Clear All'
const NO_OPTIONS_TEXT = 'No options found'
const SELECTED_COUNT_SUFFIX = 'selected'
const MIN_WIDTH_CLASS = 'min-w-[200px]'

export interface MultiSelectProps {
  label?: string
  className?: string
  options: Option[]
  value?: string[]
  defaultValue?: string[]
  onChange?: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
  fullWidth?: boolean
  clearable?: boolean
  searchable?: boolean
  maxHeight?: number
  valueClassName?: string
  placeholderClassName?: string
}

// ========== Primitive Components ==========
const PopoverRoot = PopoverPrimitive.Root
const PopoverTrigger = PopoverPrimitive.Trigger
const PopoverPortal = PopoverPrimitive.Portal
const PopoverContent = PopoverPrimitive.Content

// ========== Helper Functions ==========
const getSelectedLabel = (
  selectedValues: string[],
  options: Option[],
  placeholder?: string
): string => {
  if (selectedValues.length === 0) {
    return placeholder || DEFAULT_PLACEHOLDER
  }
  if (selectedValues.length === 1) {
    const option = options.find((opt) => opt.value === selectedValues[0])
    return typeof option?.label === 'string'
      ? option.label
      : `1 ${SELECTED_COUNT_SUFFIX}`
  }
  return `${selectedValues.length} ${SELECTED_COUNT_SUFFIX}`
}

const filterOptions = (options: Option[], query: string): Option[] => {
  if (!query.trim()) return options

  const lowerQuery = query.toLowerCase()
  return options.filter((option) => {
    const label = typeof option.label === 'string' ? option.label : ''
    return (
      label.toLowerCase().includes(lowerQuery) ||
      option.value.toLowerCase().includes(lowerQuery)
    )
  })
}

// ========== Main Component ==========
export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      label,
      className,
      options,
      value,
      defaultValue = [],
      onChange,
      placeholder = DEFAULT_PLACEHOLDER,
      disabled = false,
      fullWidth = false,
      clearable = true,
      searchable = false,
      maxHeight = DEFAULT_MAX_HEIGHT,
      valueClassName,
      placeholderClassName,
    },
    ref
  ) => {
    // State management
    const [open, setOpen] = useState(false)
    const [selectedValues, setSelectedValues] = useState<string[]>(
      value || defaultValue
    )
    const [searchQuery, setSearchQuery] = useState('')
    const debouncedSearchQuery = useDebounce(
      searchQuery,
      SEARCH_DEBOUNCE_DELAY_MS
    )

    // Refs
    const searchInputRef = useRef<HTMLInputElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    // Sync with external value prop
    useEffect(() => {
      if (value !== undefined) {
        setSelectedValues(value)
      }
    }, [value])

    // Focus search input when opening
    useEffect(() => {
      if (open && searchable && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus()
        }, FOCUS_DELAY_MS)
      }
    }, [open, searchable])

    // Filtered options based on search
    const filteredOptions = useMemo(
      () => filterOptions(options, debouncedSearchQuery),
      [options, debouncedSearchQuery]
    )

    // Event handlers
    const handleToggleOption = useCallback(
      (optionValue: string) => {
        const newValues = selectedValues.includes(optionValue)
          ? selectedValues.filter((v) => v !== optionValue)
          : [...selectedValues, optionValue]

        setSelectedValues(newValues)
        onChange?.(newValues)
      },
      [selectedValues, onChange]
    )

    const handleSelectAll = useCallback(() => {
      const allValues = filteredOptions
        .filter((opt) => !opt.disabled)
        .map((opt) => opt.value)
      const newValues = Array.from(new Set([...selectedValues, ...allValues]))
      setSelectedValues(newValues)
      onChange?.(newValues)
    }, [filteredOptions, selectedValues, onChange])

    const handleClearAll = useCallback(() => {
      setSelectedValues([])
      onChange?.([])
      setSearchQuery('')
    }, [onChange])

    const handleClearSelected = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()
        handleClearAll()
      },
      [handleClearAll]
    )

    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value)
      },
      []
    )

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
          setOpen(false)
        } else if (e.key === 'Enter' && !searchable) {
          e.preventDefault()
          setOpen(!open)
        }
      },
      [open, searchable]
    )

    const handleOpenChange = useCallback((newOpen: boolean) => {
      setOpen(newOpen)
      if (!newOpen) {
        setSearchQuery('')
      }
    }, [])

    // Computed values
    const displayLabel = getSelectedLabel(selectedValues, options, placeholder)
    const hasSelection = selectedValues.length > 0
    const showClearButton = clearable && hasSelection && !disabled

    return (
      <div
        className={cn('relative', fullWidth ? 'w-full' : 'w-auto', className)}
      >
        {label && (
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <PopoverRoot open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              ref={ref}
              type="button"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-label={label || DEFAULT_PLACEHOLDER}
              disabled={disabled}
              onKeyDown={handleKeyDown}
              className={cn(
                'flex h-10 w-full items-center justify-between rounded-md border border-solid border-[--Colors-Gray-light-300] bg-white  px-3 py-2 text-sm ring-offset-background',
                'hover:border-blue-400',
                'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'data-[placeholder]:text-muted-foreground',
                fullWidth ? 'w-full' : MIN_WIDTH_CLASS,
                className
              )}
            >
              <span
                className={cn(
                  'truncate',
                  hasSelection ? valueClassName : placeholderClassName
                )}
                data-placeholder={!hasSelection}
              >
                {displayLabel}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                {showClearButton && (
                  <Button
                    type="button"
                    variant="text"
                    onClick={handleClearSelected}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="flex items-center justify-center rounded-sm p-0.5 gap-0 hover:bg-accent"
                    aria-label="Clear selection"
                    leadingIcon={<X className="size-3.5 text-gray-500" />}
                  />
                )}
                <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
              </div>
            </button>
          </PopoverTrigger>
          <PopoverPortal>
            <PopoverContent
              ref={contentRef}
              className={cn(
                'z-50 w-[var(--radix-popover-trigger-width)] rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-none',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2'
              )}
              align="start"
              sideOffset={4}
              onOpenAutoFocus={(e) => {
                if (!searchable) {
                  e.preventDefault()
                }
              }}
            >
              <div className="flex flex-col">
                {/* Search Input */}
                {searchable && (
                  <div className="p-2 border-b">
                    <Input
                      ref={searchInputRef}
                      placeholder={DEFAULT_SEARCH_PLACEHOLDER}
                      value={searchQuery}
                      onChange={handleSearchChange}
                      leadingIcon={<Search className="w-4 h-4 text-gray-400" />}
                      className="h-8"
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-between gap-2 p-2 border-b">
                  <Button
                    type="button"
                    variant="text"
                    onClick={handleSelectAll}
                    disabled={
                      filteredOptions.filter((opt) => !opt.disabled).length ===
                      0
                    }
                    className="text-xs h-7 px-2"
                  >
                    {SELECT_ALL_TEXT}
                  </Button>
                  <Button
                    type="button"
                    variant="text"
                    onClick={handleClearAll}
                    disabled={selectedValues.length === 0}
                    className="text-xs h-7 px-2"
                  >
                    {CLEAR_ALL_TEXT}
                  </Button>
                </div>

                {/* Options List */}
                <div
                  className="overflow-y-auto p-1"
                  style={{ maxHeight: `${maxHeight}px` }}
                  aria-label="Options list"
                >
                  {filteredOptions.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      {NO_OPTIONS_TEXT}
                    </div>
                  ) : (
                    filteredOptions.map((option) => {
                      const isSelected = selectedValues.includes(option.value)
                      const isDisabled = option.disabled || false

                      return (
                        <button
                          key={option.value}
                          className={cn(
                            'flex items-center text-left w-full bg-transparent gap-2 px-2 py-2 rounded-sm cursor-pointer select-none',
                            'hover:bg-accent hover:text-accent-foreground',
                            'focus:bg-accent focus:text-accent-foreground focus:outline-none',
                            isDisabled &&
                              'opacity-50 cursor-not-allowed hover:bg-transparent'
                          )}
                          onClick={() => {
                            if (!isDisabled) {
                              handleToggleOption(option.value)
                            }
                          }}
                          onKeyDown={(e) => {
                            if (
                              !isDisabled &&
                              (e.key === 'Enter' || e.key === ' ')
                            ) {
                              e.preventDefault()
                              handleToggleOption(option.value)
                            }
                          }}
                          tabIndex={isDisabled ? -1 : 0}
                        >
                          <Checkbox
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => {
                              if (!isDisabled) {
                                handleToggleOption(option.value)
                              }
                            }}
                            aria-label={`Select ${option.value}`}
                            className="pointer-events-none"
                          />
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {option.iconLeft && (
                              <span className="shrink-0">
                                {option.iconLeft}
                              </span>
                            )}
                            <span className="truncate flex-1">
                              {option.label}
                            </span>
                            {option.iconRight && (
                              <span className="shrink-0">
                                {option.iconRight}
                              </span>
                            )}
                          </div>
                          {isSelected && (
                            <Check className="w-4 h-4 shrink-0 text-primary" />
                          )}
                        </button>
                      )
                    })
                  )}
                </div>
              </div>
            </PopoverContent>
          </PopoverPortal>
        </PopoverRoot>
      </div>
    )
  }
)

MultiSelect.displayName = 'MultiSelect'

export { PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger }
